import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Smartphone, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/format";

export default function AppVersionPanel() {
  const [ver, setVer] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/app/version");
    setVer(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!ver.latest_version || !ver.latest_version_code) {
      return toast.error("Version and version code are required");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/app/version", {
        latest_version: ver.latest_version,
        latest_version_code: parseInt(ver.latest_version_code, 10),
        download_url: ver.download_url || "",
        min_supported_version_code: parseInt(ver.min_supported_version_code || 1, 10),
        changelog: ver.changelog || "",
      });
      setVer(data);
      toast.success("Version metadata updated — phones will see it on next launch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (!ver) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Smartphone className="w-5 h-5 text-indigo-600" />
          <h2 className="font-display font-bold text-lg text-slate-900">Android APK release</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Fill this after uploading a new APK. The Android app checks this endpoint on every launch and prompts users to update.
        </p>

        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Latest version</Label>
              <Input
                data-testid="app-ver-name"
                value={ver.latest_version || ""}
                onChange={(e) => setVer({ ...ver, latest_version: e.target.value })}
                placeholder="1.0.1" className="mt-1.5 h-11"
              />
              <p className="text-[11px] text-slate-500 mt-1">Displayed to users. E.g. 1.0.1</p>
            </div>
            <div>
              <Label>Latest version code</Label>
              <Input
                data-testid="app-ver-code" type="number" min="1"
                value={ver.latest_version_code || ""}
                onChange={(e) => setVer({ ...ver, latest_version_code: e.target.value })}
                placeholder="2" className="mt-1.5 h-11"
              />
              <p className="text-[11px] text-slate-500 mt-1">Must match Android <code className="bg-slate-100 px-1 rounded">versionCode</code>.</p>
            </div>
          </div>

          <div>
            <Label>Download URL</Label>
            <Input
              data-testid="app-ver-url" type="url"
              value={ver.download_url || ""}
              onChange={(e) => setVer({ ...ver, download_url: e.target.value })}
              placeholder="https://example.com/jobticker-1.0.1.apk" className="mt-1.5 h-11"
            />
            <p className="text-[11px] text-slate-500 mt-1">Direct link to the signed .apk. Must be reachable from user phones.</p>
          </div>

          <div>
            <Label>Minimum supported version code</Label>
            <Input
              data-testid="app-ver-min" type="number" min="1"
              value={ver.min_supported_version_code || 1}
              onChange={(e) => setVer({ ...ver, min_supported_version_code: e.target.value })}
              className="mt-1.5 h-11 max-w-40"
            />
            <p className="text-[11px] text-slate-500 mt-1">Phones on a version below this will be shown a blocking "Update required" dialog.</p>
          </div>

          <div>
            <Label>What's new (changelog)</Label>
            <Textarea
              data-testid="app-ver-changelog"
              value={ver.changelog || ""}
              onChange={(e) => setVer({ ...ver, changelog: e.target.value })}
              placeholder="• Faster photo uploads&#10;• Fix overdue tint on Android 12"
              className="mt-1.5 min-h-24"
            />
            <p className="text-[11px] text-slate-500 mt-1">Shown inside the update dialog on the phone.</p>
          </div>

          <div className="pt-2">
            <Button type="submit" disabled={saving} data-testid="app-ver-save"
              className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              Publish version
            </Button>
            {ver.updated_at && (
              <span className="ml-3 text-xs text-slate-500">Last updated {formatDate(ver.updated_at)}</span>
            )}
          </div>
        </form>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white p-6">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 border border-white/20 px-2 py-0.5 text-[11px] font-semibold">
          <Info className="w-3 h-3" /> Current published
        </div>
        <div className="mt-3">
          <div className="text-white/70 text-xs">Version</div>
          <div className="font-display font-bold text-4xl">{ver.latest_version || "—"}</div>
        </div>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-white/70">Version code</span><span className="font-semibold tabular-nums">{ver.latest_version_code || "—"}</span></div>
          <div className="flex justify-between"><span className="text-white/70">Min supported</span><span className="font-semibold tabular-nums">{ver.min_supported_version_code || "—"}</span></div>
        </div>
        {ver.download_url ? (
          <a href={ver.download_url} target="_blank" rel="noreferrer"
            className="mt-5 block text-center bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg py-2 text-sm font-semibold transition-colors">
            Test download link
          </a>
        ) : (
          <div className="mt-5 rounded-lg bg-white/10 border border-white/20 py-2 text-center text-xs text-white/80">
            No download URL set yet
          </div>
        )}
        <div className="mt-6 border-t border-white/10 pt-4 text-xs text-white/70">
          <div className="font-semibold text-white mb-1">Endpoint</div>
          <code className="bg-white/10 rounded px-1.5 py-0.5">GET /api/app/version</code>
        </div>
      </div>
    </div>
  );
}
